import useApi from '../../../../../../utils/AxiosWrapper';
import {
  ApiError,
  unwrapApiResponse,
} from '../../../../../../utils/unwrapApiResponse';
import {
  FetchTransactionsRequest,
  TransactionWithPayments,
  TruckAttributionInput,
} from '../../../../../../types/apiTypes';
import {
  useMutation,
  useQueryClient,
  useQuery,
  UseMutationOptions,
} from '@tanstack/react-query';
import axios from 'axios';
import {
  DepositInvoicePresignedUrlRequest,
  DepositInvoicePresignedUrlResponse,
  ConfirmDepositInvoiceUploadRequest,
  ConfirmDepositInvoiceUploadResponse,
} from '../types/bankingTypes';

interface UseTransactionsParams {
  userId?: string;
  offset: number;
  limit: number;
  searchQuery?: string;
  startDate?: string;
  endDate?: string;
  isDateDescending: boolean;
  isAmountDescending: boolean;
  category?: string;
  truckId?: string;
  orderBy: string;
}

export const useTransactionsQuery = (
  params: UseTransactionsParams,
  enabled: boolean
) => {
  const { callProtectedApi } = useApi();

  return useQuery<TransactionWithPayments[]>({
    queryKey: ['transactions', params.userId ?? '', params],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/transactions/list/${params.userId ?? ''}`,
        'post',
        {
          offset: params.offset,
          limit: params.limit,
          searchQuery:
            params.searchQuery && params.searchQuery.length > 0
              ? params.searchQuery
              : undefined,
          startDate:
            params.startDate && params.startDate.length > 0
              ? params.startDate
              : undefined,
          endDate:
            params.endDate && params.endDate.length > 0
              ? params.endDate
              : undefined,
          isDateDescending: params.isDateDescending,
          isAmountDescending: params.isAmountDescending,
          category: params.category?.length ? params.category : undefined,
          truck_id: params.truckId?.length ? params.truckId : undefined,
          orderBy: params.orderBy,
        } as FetchTransactionsRequest
      );
      // Throws on failure (null response / >=400 / missing payload) so a
      // dead endpoint reaches React Query's error state instead of rendering
      // a legit-looking empty list. A genuinely empty 2xx still resolves [].
      return (
        unwrapApiResponse<{ transactions: TransactionWithPayments[] }>(
          response,
          'Failed to load transactions'
        ).transactions ?? []
      );
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useTransactionsCountQuery = (
  params: UseTransactionsParams,
  enabled: boolean
) => {
  const { callProtectedApi } = useApi();

  return useQuery<number>({
    queryKey: ['transactionsCount', params.userId ?? '', params],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/transactions/count/${params.userId ?? ''}`,
        'post',
        {
          offset: params.offset,
          limit: params.limit,
          searchQuery:
            params.searchQuery && params.searchQuery.length > 0
              ? params.searchQuery
              : undefined,
          startDate:
            params.startDate && params.startDate.length > 0
              ? params.startDate
              : undefined,
          endDate:
            params.endDate && params.endDate.length > 0
              ? params.endDate
              : undefined,
          isDateDescending: params.isDateDescending,
          isAmountDescending: params.isAmountDescending,
          category: params.category || undefined,
          truck_id: params.truckId || undefined,
        } as FetchTransactionsRequest
      );
      // `?? 0`, not `|| []`: this is a number query — the old array fallback
      // was a bug on top of masking failures.
      return (
        unwrapApiResponse<{ count: number }>(
          response,
          'Failed to load transactions count'
        ).count ?? 0
      );
    },
    enabled,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useTransactionMutations = (
  userId: string | undefined,
  currentParams: UseTransactionsParams
) => {
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();

  const invalidateRelatedQueries = () => {
    const keysToInvalidate = [
      ['recentExpenses', userId ?? ''],
      ['recentDeposits', userId ?? ''],
      ['budgetItems', userId ?? ''],
      ['monthlyBudgetMetrics', userId ?? ''],
      ['dollarBreakdownData', userId ?? ''],
      ['trucksBudgetedSpentData', userId ?? ''],
      ['revenueExpensesData', userId ?? ''],
      ['transactions', userId ?? ''],
      ['transactionsCount', userId ?? ''],
    ] as const;

    keysToInvalidate.forEach((queryKey) => {
      queryClient.invalidateQueries({ queryKey });
    });

    // Double invalidation for eventual consistency
    setTimeout(() => {
      keysToInvalidate.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    }, 3000);
  };

  // Real useMutation (not plain async functions): failed PATCHes reject
  // through mutateAsync AND reach the global MutationCache.onError with a
  // mutation_key tag — a plain async throw would bypass Sentry entirely.
  const patchTransaction = async (
    transaction_id: string,
    body: Record<string, unknown>,
    fallbackMsg: string
  ) => {
    const response = await callProtectedApi(
      `/transactions/${transaction_id}`,
      'patch',
      body
    );
    unwrapApiResponse(response, fallbackMsg);
  };

  // Shared config for the simple PATCH mutations (the category one is separate
  // because it also does an optimistic update + rollback). Each supplies its
  // key, how to build the PATCH body, and its fallback message.
  const patchMutationOptions = <V extends { transaction_id: string }>(
    mutationKey: string[],
    toBody: (vars: V) => Record<string, unknown>,
    fallbackMsg: string
  ): UseMutationOptions<void, unknown, V> => ({
    mutationKey,
    mutationFn: (vars) =>
      patchTransaction(vars.transaction_id, toBody(vars), fallbackMsg),
    onError: (e) => console.error(`${fallbackMsg}:`, e),
    onSuccess: () => invalidateRelatedQueries(),
  });

  const categoryMutation = useMutation({
    mutationKey: ['updateTransactionCategory'],
    mutationFn: ({
      transaction_id,
      newCategory,
    }: {
      transaction_id: string;
      newCategory: string;
    }) =>
      patchTransaction(
        transaction_id,
        { category: newCategory },
        'Failed to update transaction category'
      ),
    onMutate: async ({ transaction_id, newCategory }) => {
      const queryKey = ['transactions', userId ?? '', currentParams];
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<TransactionWithPayments[]>(queryKey);

      // Optimistic update
      queryClient.setQueryData<TransactionWithPayments[]>(queryKey, (old) => {
        if (!old) return [];
        return old.map((t) =>
          t.transaction_id === transaction_id
            ? { ...t, category: newCategory }
            : t
        );
      });

      return { queryKey, previousData };
    },
    onError: (err, _vars, context) => {
      console.error('Error updating category:', err);
      if (context) {
        queryClient.setQueryData(context.queryKey, context.previousData ?? []);
      }
    },
    onSuccess: invalidateRelatedQueries,
  });

  const truckMutation = useMutation(
    patchMutationOptions<{ transaction_id: string; newTruckId: string }>(
      ['updateTransactionTruck'],
      ({ newTruckId }) => ({ truck_id: newTruckId }),
      'Failed to update transaction truck'
    )
  );

  const attributionsMutation = useMutation(
    patchMutationOptions<{
      transaction_id: string;
      attributions: TruckAttributionInput[];
    }>(
      ['updateTruckAttributions'],
      ({ attributions }) => ({ truck_attributions: attributions }),
      'Failed to update truck attributions'
    )
  );

  const updateCategory = (transaction_id: string, newCategory: string) =>
    categoryMutation.mutateAsync({ transaction_id, newCategory });

  const updateTruck = (transaction_id: string, newTruckId: string) =>
    truckMutation.mutateAsync({ transaction_id, newTruckId });

  const updateTruckAttributions = (
    transaction_id: string,
    attributions: TruckAttributionInput[]
  ) => attributionsMutation.mutateAsync({ transaction_id, attributions });

  return { updateCategory, updateTruck, updateTruckAttributions };
};

export const useUploadInvoiceMutation = () => {
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['uploadInvoice'],
    mutationFn: async ({
      transactionId,
      file,
      onProgress,
    }: {
      transactionId: string;
      file: File;
      onProgress?: (progress: number) => void;
    }) => {
      // 1. Request Presigned URL
      const fileType =
        file.type === 'application/pdf'
          ? 'pdf'
          : (file.type.split('/')[1] as any);
      const presignedRes = await callProtectedApi(
        '/depositInvoices/presigned-url',
        'post',
        {
          transaction_id: transactionId,
          file_type: fileType,
        } as DepositInvoicePresignedUrlRequest
      );

      const { presigned_url } =
        unwrapApiResponse<DepositInvoicePresignedUrlResponse>(
          presignedRes,
          'Failed to get presigned URL'
        );

      // 2. Upload to S3
      // 2. Upload to S3

      // Fake progress implementation
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 10;
        if (progress > 90) progress = 90;
        if (onProgress) onProgress(Math.round(progress));
      }, 200);

      try {
        await axios.put(presigned_url, file, {
          headers: {
            'Content-Type': file.type,
          },
        });
      } finally {
        clearInterval(interval);
      }

      if (onProgress) onProgress(100);

      // 3. Confirm Upload
      const confirmRes = await callProtectedApi(
        '/depositInvoices/confirm-upload',
        'post',
        {
          transaction_id: transactionId,
        } as ConfirmDepositInvoiceUploadRequest
      );

      const confirmData =
        unwrapApiResponse<ConfirmDepositInvoiceUploadResponse>(
          confirmRes,
          'Failed to confirm invoice upload'
        );
      if (!confirmData.success) {
        // Distinct message + tags: a 2xx confirm that reports success=false is
        // a different failure from the unwrap error above.
        throw new ApiError(
          'Invoice upload confirmation returned success=false',
          {
            status: confirmRes?.status,
            url: confirmRes?.config?.url,
          }
        );
      }

      return confirmData;
    },
    onSuccess: (data, variables) => {
      // Invalidate transactions to reflect the new invoice
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
    },
    onError: (error: any) => {
      console.log('Error uploading invoice:', error);
    },
  });
};

export const useGetInvoiceMutation = () => {
  const { callProtectedApi } = useApi();
  return useMutation({
    mutationKey: ['getInvoice'],
    mutationFn: async (transactionId: string) => {
      const response = await callProtectedApi(
        `/depositInvoices/transaction/${transactionId}`,
        'get'
      );

      return unwrapApiResponse<DepositInvoicePresignedUrlResponse>(
        response,
        'Failed to get invoice'
      );
    },
  });
};
